import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Lock, AlertCircle, Eye, EyeOff, Calendar } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // MODIF : Nouvel état pour le blocage
  const [retryAfter, setRetryAfter] = useState(0);

  // MODIF : Timer pour le compte à rebours
  useEffect(() => {
    if (retryAfter <= 0) return;
    const interval = setInterval(() => {
      setRetryAfter((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [retryAfter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (retryAfter > 0) return; // Sécurité
    setError("");
    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.append("grant_type", "");
      params.append("username", email);
      params.append("password", password);
      params.append("scope", "");
      params.append("client_id", "");
      params.append("client_secret", "");

      const response = await axios.post(`${API}/auth/login`, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "accept": "application/json"
        },
      });

      const { access_token, refresh_token, user_type } = response.data;

      // Clear admin session if login as user
      localStorage.removeItem("admin_auth");
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_info");

      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("user_type", user_type);

      toast.success("Connexion réussie !");
      navigate("/");
    } catch (err) {
      console.error(err);
      // MODIF : Gestion de l'erreur 429 avec Axios
      if (err.response?.status === 429) {
        const waitTime = parseInt(err.response.headers['retry-after']) || 60;
        setRetryAfter(waitTime);
        setError(`Trop de tentatives. Veuillez patienter ${waitTime} secondes.`);
      } else {
        setError(err.response?.data?.detail || "Email ou mot de passe incorrect");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-8" data-testid="login-page">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-foreground/40 hover:text-primary transition-colors font-semibold uppercase tracking-widest text-xs"
            data-testid="back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <span className="text-2xl font-bold text-primary">Sequenti</span>
        </div>

        <Card className="p-12 border-none bg-white shadow-xl rounded-lg border border-border/40 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-accent" />
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/5 rounded-full mb-6">
              <Lock className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-4xl font-black text-primary mb-3">
              Connexion
            </h1>
            <p className="text-lg text-foreground/40 font-medium">
              Accédez à votre espace Sequenti
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {error && (
              <div className="flex items-center gap-3 p-4 bg-destructive/5 text-destructive rounded-lg border border-destructive/10 text-base font-semibold">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            <div className="space-y-3">
              <Label htmlFor="email" className="text-base font-bold text-primary">Email</Label>
              <Input
                id="email"
                type="email"
                className="h-14 text-lg rounded-lg border-border/50"
                disabled={retryAfter > 0}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                data-testid="input-email"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-base font-bold text-primary">Mot de passe</Label>
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm font-semibold text-accent hover:underline"
                >
                  Mot de passe oublié ?
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className="h-14 text-lg rounded-lg border-border/50 pr-12"
                  disabled={retryAfter > 0}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-primary transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              // On change la couleur en rouge (destructive) si bloqué
              className={`w-full text-xl h-16 rounded-lg shadow-lg transition-all active:scale-95 mt-4 ${retryAfter > 0 ? "bg-destructive/60 cursor-not-allowed" : "bg-primary text-white"
                }`}
              // On désactive si chargement EN COURS ou si RETRYAFTER > 0
              disabled={loading || retryAfter > 0}
              data-testid="login-button"
            >
              {loading
                ? "Connexion en cours..."
                : retryAfter > 0
                  ? `Réessayer dans ${retryAfter}s`
                  : "Se connecter"}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border/50">
            <Button
              variant="outline"
              className="w-full border-accent text-accent hover:bg-accent/5 text-lg h-14 rounded-lg flex items-center justify-center gap-3"
              onClick={() => window.open('https://calendly.com/eric-sequenti/30min', '_blank')}
            >
              <Calendar className="w-5 h-5" />
              Prendre rendez-vous avec Éric
            </Button>
          </div>

          <div className="mt-8 text-center">
            <p className="text-foreground/60">
              Pas encore de compte ?{" "}
              <button
                onClick={() => navigate("/repreneur")}
                className="text-accent font-bold hover:underline"
              >
                Commencer une évaluation
              </button>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
